import { type ProductOption } from "@kanpla/api-contract";
import { type AllergyMatcher } from "@kanpla/allergens";

import { type Currency } from "~/api/products/types";
import { priceFormatter } from "~/utils/price-formatter";
import { DEFAULT_LANGUAGE } from "~/utils/constants";
import { calculateUnitGrossPrice } from "~/utils/calculate-unit-gross-price";

import { type VariantFormData } from "../route";

import { VariantHint } from "./variant-hint";
import { ChoiceInput } from "./choice-input";

type VariantFormProps = {
  variant: ProductOption;
  variantFormData: VariantFormData | null;
  onChange: (data: VariantFormData) => void;
  isError: boolean;
  currency: Currency;
  locale: string | null;
  vatRate: number;
  matchAllergies: AllergyMatcher;
};

export const VariantForm = ({
  variant,
  variantFormData,
  onChange,
  isError,
  currency,
  locale,
  vatRate,
  matchAllergies
}: VariantFormProps) => {
  const initialFormData = variantFormData || {};

  const totalQuantity = Object.values(initialFormData).reduce(
    (acc, c) => acc + (c || 0),
    0
  );
  const maxReached =
    variant.maximum !== null && totalQuantity >= variant.maximum;

  const toggleChoice = (id: string) => {
    const existing = initialFormData?.[id] || 0;

    if (existing) {
      onChange({ ...initialFormData, [id]: 0 });
      return;
    }

    if (maxReached) {
      if (variant.maximum === 1) {
        // for single choice options, we can just switch choice
        onChange({ [id]: 1 });
      }
      return;
    }

    onChange({ ...initialFormData, [id]: 1 });
  };

  const updateQuantity = (id: string, change: number) => {
    if (change > 0 && maxReached) return;
    const existing = initialFormData?.[id] || 0;
    onChange({ ...initialFormData, [id]: Math.max(existing + change, 0) });
  };

  return (
    <div className="max-w-lg flex-1">
      <div className="mb-2 flex items-center justify-between px-1">
        <p className="text-lg font-bold">{variant.name}</p>
        <VariantHint
          minimum={variant.minimum}
          maximum={variant.maximum}
          error={isError}
        />
      </div>
      <div className="min-h-96 rounded-xl border border-interface-neutral-3 bg-interface-neutral-0 px-5 py-2">
        {variant.choices.map((choice) => {
          const isSelected = Boolean(initialFormData?.[choice.id]);
          const quantity =
            isSelected && initialFormData?.[choice.id]
              ? initialFormData[choice.id]
              : 1;

          const unitPriceInclVat = calculateUnitGrossPrice(
            choice.unitPriceExclVat,
            vatRate,
            null
          );

          return (
            <ChoiceInput
              key={choice.id}
              choice={choice}
              variantMaximum={variant.maximum}
              checked={isSelected}
              onItemClick={() => {
                toggleChoice(choice.id);
              }}
              quantity={quantity}
              onQuantityIncrease={() => {
                updateQuantity(choice.id, 1);
              }}
              onQuantityDecrease={() => {
                updateQuantity(choice.id, -1);
              }}
              displayPrice={
                unitPriceInclVat > 0
                  ? priceFormatter({
                      value: unitPriceInclVat,
                      currency,
                      locale: locale ?? DEFAULT_LANGUAGE
                    })
                  : undefined
              }
              displayDeposit={
                choice.deposit
                  ? priceFormatter({
                      value: choice.deposit.feeInclVat,
                      currency,
                      locale: locale ?? DEFAULT_LANGUAGE
                    })
                  : undefined
              }
              matchAllergies={matchAllergies}
            />
          );
        })}
      </div>
    </div>
  );
};
